/**
 * UpgradePrompt Component
 *
 * Displays upgrade CTA for redirect gating behavior.
 * Used when content should redirect to upgrade flow instead of blur.
 */

import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { createBoxShadow } from '../../lib/design-tokens';
import { useTranslation } from '@dramadetector/shared';
import { GlassButton } from '../glass/GlassButton';
import { TIER_DISPLAY_NAMES, type UpgradePromptProps } from './types';

export function UpgradePrompt({
  targetTier,
  onUpgrade,
  testID,
  featureKey,
  price,
}: UpgradePromptProps): React.ReactNode {
  const { t } = useTranslation('features');
  const tierName = TIER_DISPLAY_NAMES[targetTier];

  const title = featureKey
    ? t(`gating.${featureKey}_title`, { defaultValue: t('gating.premium_feature_title') })
    : t('gating.premium_feature_title');

  const description = featureKey
    ? t(`gating.${featureKey}_body`, { defaultValue: t('gating.premium_feature_description', { tierName }) })
    : t('gating.premium_feature_description', { tierName });

  const ctaParams = price ? { price } : { price: '' };
  const cta = featureKey
    ? t(`gating.${featureKey}_cta`, { ...ctaParams, defaultValue: t('gating.upgrade_to_tier', { tierName }) })
    : t('gating.upgrade_to_tier', { tierName });

  return (
    <View style={styles.container} testID={testID}>
      <View style={styles.card} testID={testID ? `${testID}-card` : undefined}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.description}>{description}</Text>
        <GlassButton
          onPress={onUpgrade}
          accessibilityRole="button"
          accessibilityLabel={cta}
          testID={testID ? `${testID}-upgrade-button` : undefined}
        >
          {cta}
        </GlassButton>
      </View>
    </View>
  );
}

// Platform-aware card shadow
const cardShadow = createBoxShadow({
  color: '#000',
  offsetX: 0,
  offsetY: 4,
  blurRadius: 12,
  opacity: 0.1,
  elevation: 4,
});

const styles = StyleSheet.create({
  container: {
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    maxWidth: 320,
    ...cardShadow,
  } as ViewStyle,
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
});
